import gql from 'graphql-tag';
import { GET_PARTICIPANTS_BY_USER, GET_PERSON_BY_EXTERNAL_ID, GET_PARTICIPANT_USERS } from '@/gql';

const GET_PERMISSIONS_QUERY = gql`
	query GetPermissionsByPersonExternalId($person_external_id: String!) {
		results: risolto_view_platform_licence_permission(where: {person_external_id: {_eq: $person_external_id}}) {
			person_external_id
			participant_user
			participant
			participant_user_role
			role_name
			role
			license_module
			permission_name
			permission
			grant_type
		}
	}
`;

export const loadAndStorePermissions = async (apolloClient, personExternalId, store) => {
	let loading = true;

	let result;
	try {
		result = await apolloClient.query({
			query: GET_PERMISSIONS_QUERY,
			variables: { person_external_id: personExternalId }
		});
	} catch (e) {
		console.error('Error fetching data: ', e);
		throw e;
	}

	loading = false;

	const licences = {};
	const permissions = {};

	result.data.results.forEach(p => {
		if (!licences[p.participant]) {
			licences[p.participant] = {};
		}
		if (!licences[p.participant][p.license_module]) {
			licences[p.participant][p.license_module.toUpperCase()] = true;
		}
		if (!permissions[p.participant]) {
			permissions[p.participant] = {};
		}
		if (!permissions[p.participant][p.license_module]) {
			permissions[p.participant][p.license_module.toUpperCase()] = {};
		}

		if (p.permission_name) {
			permissions[p.participant][p.license_module.toUpperCase()][p.permission_name.toUpperCase()] = p.grant_type === 'ALLOW';
		}
	});

	store.commit('risolto/SET_LICENCES', licences);
	store.commit('risolto/SET_PERMISSIONS', permissions);

	return {
		loading,
		licences,
		permissions
	};
};

export const hasLicenseAndPermission = (licences, permissions, required_licence: string | null = null, required_permission: string | null = null) => {
	if (!licences || !permissions) {
		return false;
	}
	licences = Object.keys(licences).reduce((acc, key) => {
		acc[key.toUpperCase()] = licences[key];
		return acc;
	}, {});
	permissions = Object.keys(permissions).reduce((acc, key) => {
		acc[key.toUpperCase()] = Object.keys(permissions[key]).reduce((accPerm, permKey) => {
			accPerm[permKey.toUpperCase()] = permissions[key][permKey];
			return accPerm;
		}, {});
		return acc;
	}, {});
	if (required_licence) {
		required_licence = required_licence.toUpperCase();
	}
	if (required_permission) {
		required_permission = required_permission.toUpperCase();
	}

	if (!licences || !permissions) {
		return false;
	}
	if (!required_licence && !required_permission) {
		return true;
	}
	if (required_licence && !required_permission) {
		return licences[required_licence];
	}
	if (required_licence && required_permission) {
		return licences[required_licence] && permissions[required_licence][required_permission];
	}

	return false;
};

export const loadAndStoreUserAndParticipants = async (apolloClient, sub, store) => {
	try {
		// Query to get the logged-in user
		const { data: userData } = await apolloClient.query({
			query: GET_PERSON_BY_EXTERNAL_ID,
			variables: {
				externalId: sub
			}
		});

		const loggedInPerson = userData.risolto_person[0];
		store.commit('risolto/SET_LOGGED_IN_USER', loggedInPerson);

		// Query to get participants by user
		const { data: participantsData } = await apolloClient.query({
			query: GET_PARTICIPANTS_BY_USER,
			variables: {
				externalId: sub,
				license_module: 'CORE'
			}
		});

		const legalEntities = [] as any;
		const userInterfaceDetails = {} as any;
		const integrations = {} as any;

		// Sort participants by name
		const sortedParticipants = [...participantsData.risolto_participant].sort((p1, p2) =>
			p1.name.localeCompare(p2.name)
		);

		// Process SSO domains per participant and legal entity
		const ssoDomains = {};

		sortedParticipants.forEach(participant => {
			const participantDomains: {
				[key: string]: string[];
			} = {};

			participant.legal_entities.forEach(legalEntity => {
				const legalEntityDomains = legalEntity.single_sign_ons.map(sso => sso.domain);

				if (legalEntityDomains.length > 0) {
					participantDomains[legalEntity.reference] = legalEntityDomains;
				}
			});

			ssoDomains[participant.reference] = participantDomains;
		});

		store.commit('risolto/SET_SSO_DOMAINS', ssoDomains);

		// Process participants and legal entities
		sortedParticipants.forEach((participant) => {
			if (participant.user_interface_details) {
				userInterfaceDetails[participant.reference] = participant.user_interface_details;
			}
			if (participant.system_integrations?.length) {
				integrations[participant.reference] = participant.system_integrations;
			}

			if (participant.legal_entities.length > 1) {
				legalEntities.push({
					key: participant.reference,
					participant: {
						reference: participant.reference,
						name: participant.name,
						logical_id: participant.logical_id
					},
					legal_entity: null
				});
			}

			participant.legal_entities.forEach((legalEntity) => {
				legalEntities.push({
					key: participant.reference + '.' + legalEntity.reference,
					participant: {
						reference: participant.reference,
						name: participant.name,
						logical_id: participant.logical_id
					},
					legal_entity: {
						reference: legalEntity.reference,
						logical_id: legalEntity.logical_id,
						registration_name: legalEntity.registration_name,
						display_name: legalEntity.display_name
					}
				});
			});
		});

		store.commit('risolto/SET_LEGAL_ENTITIES', legalEntities);
		store.commit('risolto/SET_USER_INTERFACE_DETAILS', userInterfaceDetails);
		store.commit('risolto/SET_INTEGRATIONS', integrations);

		// Set the selected participant if it's null
		if (store.getters.selectedParticipantStore == null) {
			store.commit('risolto/SET_SELECTED_PARTICIPANT', legalEntities[0].participant.reference);
		}

		// Set the selected legal entity if it's null
		if (store.getters.selectedLegalEntityStore == null) {
			const filteredLegalEntities = legalEntities.filter(
				(legalEntity) => legalEntity.participant.reference === store.getters.selectedParticipantStore
			);
			store.commit('risolto/SET_SELECTED_LEGAL_ENTITY', filteredLegalEntities[0].legal_entity?.reference);
		}

		// Fetch participant users for the selected participant/legal entity
		await fetchAndStoreParticipantUsers(
			apolloClient,
			store,
			store.getters.selectedParticipantStore,
			store.getters.selectedLegalEntityStore
		);
	} catch (error) {
		console.error('Error loading user and participants:', error);
	}
};

// Helper function to fetch and store participant users
export const fetchAndStoreParticipantUsers = async (apolloClient, store, participant, legalEntity) => {
	if (!participant) {
		return;
	}

	try {
		const variables = !legalEntity
			? { participant: participant }
			: { participant: participant, legal_entity: legalEntity };

		const result = await apolloClient.query({
			query: GET_PARTICIPANT_USERS,
			variables: variables
		});

		if (result && result.data && result.data.participantUsers) {
			// Make users unique (multiple participant_user entries per person)
			const seen = new Map();
			const participantUsers = result.data.participantUsers || [];

			participantUsers.forEach(element => {
				seen.set(element.person_by_reference.reference, element);
			});

			// Sort users by username
			const sortedUsers = [...seen.values()].sort((a, b) => {
				return a.person_by_reference.username < b.person_by_reference.username ? -1 : 1;
			});

			store.commit('risolto/SET_PARTICIPANT_USERS', sortedUsers);
		}
	} catch (error) {
		console.error('Error fetching participant users:', error);
	}
};
